import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Smartphone, Shield } from 'lucide-react';

const LoginPage = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const user = await login(formData.email, formData.password);
            toast.success('Connexion réussie');
            
            if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.status === 'approved') {
                navigate('/catalog');
            } else {
                navigate('/catalog');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur de connexion';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <Link 
                    to="/" 
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 font-medium transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                </Link>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <Smartphone className="w-8 h-8 text-white" />
                        </div>
                        <h1 
                            className="text-3xl font-bold text-gray-900 mb-2"
                            style={{ fontFamily: 'var(--font-heading)' }}
                        >
                            Connexion
                        </h1>
                        <p className="text-gray-500">
                            Accédez à votre espace professionnel
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                data-testid="login-email"
                                className="h-12 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    data-testid="login-password"
                                    className="h-12 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-200 pr-12"
                                />
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Link 
                                to="/forgot-password" 
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                data-testid="forgot-password-link"
                            >
                                Mot de passe oublié ?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-lg bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-colors"
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                'Se connecter'
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-gray-500">
                            Pas encore de compte ?{' '}
                            <Link 
                                to="/register" 
                                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                                data-testid="go-to-register"
                            >
                                Demander un accès
                            </Link>
                        </p>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <div className="inline-flex items-center gap-2 text-xs text-gray-400">
                        <Shield className="w-3 h-3" />
                        <span>Plateforme réservée aux professionnels</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
